import { FeatureFlag } from '@/constants/feature-flags.ts';
import { makeStyles } from '@material-ui/core/styles';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useChainConfigs } from '@/hooks/use-chain-configs.ts';
import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';

import { sectionHeading } from '@/styles/sectionHeading.ts';

import { useCategoryBrowser } from '@/hooks/use-category-browser.ts';
import { getText } from '@/utils/helpers.js';
import { formatInsuranceType, isTreatmentAvailableForInsurance } from '@/utils/insurance-utils.ts';

import {
    selectChainConfig,
    selectSingleTreatment,
    useConfigStore,
    usePersistedPrismicStore,
    useSearchStore,
} from '@/store.js';

import TreatmentPill from './TreatmentPill.jsx';

const COLLAPSED_LIMIT = 15;
const TREATMENT_LIST_ID = 'location-treatments-list';

const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        width: '100%',
        maxWidth: theme.layout.contentWidth,
        margin: '0 auto',
        paddingTop: '2rem',
    },
    heading: sectionHeading(theme),
    pills: {
        // A grid rather than a wrap: every row fills its column, so the list reads as even columns
        // instead of chips of different widths breaking wherever they happen to fit.
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    showAll: {
        padding: 0,
        border: 'none',
        background: 'none',
        fontFamily: 'inherit',
        fontSize: '0.875rem',
        fontWeight: theme.weight.body,
        color: theme.palette.common.darkGrey,
        textDecoration: 'underline',
        textDecorationColor: theme.palette.common.yellow,
        textDecorationThickness: 2,
        textUnderlineOffset: '0.2em',
        cursor: 'pointer',
        '&:hover, &:focus-visible': {
            textDecorationThickness: 3,
        },
    },
}));

const LocationTreatments = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const treatments = useSearchStore((state) => state.treatments);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const selectedHealthInsurance = useSearchStore((state) => state.selectedHealthInsurance);
    const professionals = useSearchStore((state) => state.professionals);
    const findAppointmentData = usePersistedPrismicStore((state) => state.findAppointmentData);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);
    const { showCategoryBrowser: hasOwnTreatmentBrowser } = useCategoryBrowser();
    const { value: isChainBookingEnabled } = useFeatureFlag(FeatureFlag.IS_CHAIN_BOOKING_ENABLED_FOR_PATIENTS);

    const insuranceType = formatInsuranceType(selectedHealthInsurance);
    const { chainConfigs } = useChainConfigs(
        Boolean(isChainBookingEnabled && insuranceType),
        currentCustomer?.customer_id?.[0]?.text,
        selectedLocation ?? currentCustomer?.locations?.[0]?.key?.[0]?.text,
        insuranceType
    );

    const browseContext = `${selectedLocation}|${selectedHealthInsurance}`;
    const [renderedContext, setRenderedContext] = useState(browseContext);
    if (renderedContext !== browseContext) {
        setRenderedContext(browseContext);
        setExpanded(false);
    }

    const offeredKeys = useMemo(() => {
        const profiles = findAppointmentData?.[i18n.language]?.professionalProfiles ?? [];
        const keys = new Set();
        profiles.forEach((professional) => {
            if (!professionals?.[getText(professional.key)]) return;
            if (getText(professional.locationData?.key) !== selectedLocation) return;
            (professional.servicesList ?? []).forEach((service) => {
                const key = getText(service.key);
                if (key) keys.add(key);
            });
        });
        return keys;
    }, [findAppointmentData, i18n.language, selectedLocation, professionals]);

    const bookable = useMemo(() => {
        const forLocation = Object.values(treatments?.[selectedLocation] ?? {});
        return forLocation
            .filter((treatment) => offeredKeys.has(getText(treatment.key)))
            .filter((treatment) => isTreatmentAvailableForInsurance(treatment, selectedHealthInsurance))
            .filter((treatment) => !!getText(treatment.name))
            .flatMap((entry) => [
                ...(entry.location ? [{ treatment: entry, isOnline: false }] : []),
                ...(entry.online ? [{ treatment: entry, isOnline: true }] : []),
            ]);
    }, [treatments, selectedLocation, selectedHealthInsurance, offeredKeys]);

    const bookableChains = isChainBookingEnabled ? chainConfigs : [];
    const items = [
        ...bookable.map(({ treatment, isOnline }) => ({
            key: `${getText(treatment.key)}-${isOnline ? 'online' : 'location'}`,
            label: getText(treatment.name),
            isOnline: isOnline,
            onSelect: () => selectSingleTreatment(getText(treatment.key), isOnline),
        })),
        ...bookableChains.map((config) => ({
            key: config.id,
            label: config.name[i18n.language] ?? config.name.de,
            badge: t('ChainStepCountBadge', { count: config.steps.length }),
            onSelect: () => selectChainConfig(config),
        })),
    ];

    if (hasOwnTreatmentBrowser || items.length === 0) return null;

    const visible = expanded ? items : items.slice(0, COLLAPSED_LIMIT);

    return (
        <div className={classes.wrapper}>
            <p className={classes.heading}>{t('TreatmentsAtThisLocation')}</p>
            <div id={TREATMENT_LIST_ID} className={classes.pills}>
                {visible.map((item) => (
                    <TreatmentPill
                        key={item.key}
                        dataTestId={`location-treatment-${item.key}`}
                        label={item.label}
                        isOnline={item.isOnline}
                        badge={item.badge}
                        onClick={item.onSelect}
                        asRow
                    />
                ))}
            </div>
            {items.length > COLLAPSED_LIMIT && (
                <button
                    type="button"
                    className={classes.showAll}
                    aria-expanded={expanded}
                    aria-controls={TREATMENT_LIST_ID}
                    onClick={() => setExpanded((isExpanded) => !isExpanded)}
                >
                    {expanded ? t('ShowFewerTreatments') : t('ShowAllTreatments')}
                </button>
            )}
        </div>
    );
};

export default LocationTreatments;
